import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';

// ============ TYPES ============

interface TerritoryStatus {
  claim: {
    id: string;
    islandId: string;
    ownerName: string;
    customName: string | null;
    claimRadius: number;
    upkeepPaidUntil: string | null;
    claimedAt: string;
    lastCollectedAt: string | null;
    accumulatedPieces: number;
    accumulatedMaterials: Record<string, number>;
    permissions: {
      allowAllies: boolean;
      allowTrading: boolean;
      allowDocking: boolean;
      pvpEnabled: boolean;
      taxRate: number;
    };
    upgrades: Array<{ type: string; level: number }>;
    assignedFaction?: string;
  };
  garrison: {
    units: Array<{
      id: string;
      type: string;
      level: number;
      strength: number;
      upkeepCost: number;
    }>;
    totalStrength: number;
    dailyUpkeep: number;
  } | null;
  island: {
    type: string;
    biome: string;
  };
}

interface GarrisonUnitDefinition {
  type: string;
  name: string;
  description: string;
  hireCost: number;
  upkeepCost: number;
  baseStrength: number;
  maxLevel: number;
}

interface TerritoryStatusPanelProps {
  isOpen: boolean;
  onClose: () => void;
  socket: Socket | null;
  islandId: string;
  islandName: string;
  playerPieces: number;
}

// ============ COMPONENT ============

export const TerritoryStatusPanel: React.FC<TerritoryStatusPanelProps> = ({
  isOpen,
  onClose,
  socket,
  islandId,
  islandName,
  playerPieces,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'garrison' | 'permissions' | 'upgrades'>('overview');
  const [territoryStatus, setTerritoryStatus] = useState<TerritoryStatus | null>(null);
  const [garrisonDefinitions, setGarrisonDefinitions] = useState<Record<string, GarrisonUnitDefinition>>({});
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  // Fetch territory status
  useEffect(() => {
    if (!socket || !isOpen || !islandId) return;

    socket.emit('territory:getStatus', { islandId });
    socket.emit('territory:getGarrisonDefinitions');

    const handleStatus = (data: { islandId: string; status: TerritoryStatus }): void => {
      if (data.islandId === islandId) {
        setTerritoryStatus(data.status);
      }
    };

    const handleGarrisonDefinitions = (data: { definitions: Record<string, GarrisonUnitDefinition> }): void => {
      setGarrisonDefinitions(data.definitions);
    };

    const handleSuccess = (data: { message: string }): void => {
      setMessage({ text: data.message, type: 'success' });
      socket.emit('territory:getStatus', { islandId });
      setTimeout(() => setMessage(null), 3000);
    };

    const handleError = (data: { message: string }): void => {
      setMessage({ text: data.message, type: 'error' });
      setTimeout(() => setMessage(null), 3000);
    };

    socket.on('territory:status', handleStatus);
    socket.on('territory:garrisonDefinitions', handleGarrisonDefinitions);
    socket.on('territory:garrisonHired', handleSuccess);
    socket.on('territory:garrisonDismissed', handleSuccess);
    socket.on('territory:garrisonUpgraded', handleSuccess);
    socket.on('territory:permissionsUpdated', handleSuccess);
    socket.on('territory:upgradeCompleted', handleSuccess);
    socket.on('territory:error', handleError);

    return () => {
      socket.off('territory:status', handleStatus);
      socket.off('territory:garrisonDefinitions', handleGarrisonDefinitions);
      socket.off('territory:garrisonHired', handleSuccess);
      socket.off('territory:garrisonDismissed', handleSuccess);
      socket.off('territory:garrisonUpgraded', handleSuccess);
      socket.off('territory:permissionsUpdated', handleSuccess);
      socket.off('territory:upgradeCompleted', handleSuccess);
      socket.off('territory:error', handleError);
    };
  }, [socket, isOpen, islandId]);

  const handleHireGarrison = useCallback((unitType: string): void => {
    if (!socket) return;
    socket.emit('territory:hireGarrison', { islandId, unitType });
  }, [socket, islandId]);

  const handleDismissGarrison = useCallback((unitId: string): void => {
    if (!socket) return;
    socket.emit('territory:dismissGarrison', { unitId });
  }, [socket]);

  const handleUpgradeGarrison = useCallback((unitId: string): void => {
    if (!socket) return;
    socket.emit('territory:upgradeGarrison', { unitId });
  }, [socket]);

  const handleUpdatePermissions = useCallback((permissions: Partial<TerritoryStatus['claim']['permissions']>): void => {
    if (!socket || !territoryStatus) return;
    socket.emit('territory:updatePermissions', { 
      claimId: territoryStatus.claim.id, 
      permissions: { ...territoryStatus.claim.permissions, ...permissions }
    });
  }, [socket, territoryStatus]);

  const handleCollectIncome = useCallback((): void => {
    if (!socket || !territoryStatus) return;
    socket.emit('territory:collectIncome', { claimId: territoryStatus.claim.id });
  }, [socket, territoryStatus]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatTimeRemaining = (dateString: string | null): string => {
    if (!dateString) return 'Never paid';
    const now = Date.now();
    const target = new Date(dateString).getTime();
    const diff = target - now;
    
    if (diff < 0) return 'OVERDUE';
    
    const days = Math.floor(diff / (24 * 60 * 60 * 1000));
    const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    
    if (days > 0) return `${days}d ${hours}h`;
    return `${hours}h`;
  };

  if (!isOpen || !territoryStatus) return null;

  const { claim, garrison, island } = territoryStatus;
  const totalDailyUpkeep = (garrison?.dailyUpkeep || 0);
  const hasIncome = claim.accumulatedPieces > 0 || Object.keys(claim.accumulatedMaterials).length > 0;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#0d1117',
          border: '2px solid #30363d',
          padding: 0,
          width: '800px',
          maxHeight: '90vh',
          overflow: 'hidden',
          fontFamily: '"Sometype Mono", monospace',
          display: 'flex',
          flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #30363d',
            backgroundColor: '#161b22',
          }}
        >
          <div>
            <span style={{ color: '#58a6ff', fontSize: '14px', fontWeight: 'bold' }}>
              {claim.customName || islandName}
            </span>
            <span style={{ color: '#8b949e', fontSize: '11px', marginLeft: '8px' }}>
              {island.type} • {island.biome}
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ color: '#ffd700', fontSize: '12px' }}>
              {playerPieces.toLocaleString()}₱
            </span>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: '1px solid #f85149',
                color: '#f85149',
                cursor: 'pointer',
                fontSize: '14px',
                padding: '4px 8px',
                fontFamily: '"Sometype Mono", monospace',
                fontWeight: 'bold',
              }}
            >
              x
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            borderBottom: '1px solid #30363d',
            backgroundColor: '#161b22',
          }}
        >
          {(['overview', 'garrison', 'permissions', 'upgrades'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                padding: '10px',
                backgroundColor: activeTab === tab ? '#0d1117' : 'transparent',
                color: activeTab === tab ? '#58a6ff' : '#8b949e',
                border: 'none',
                borderBottom: activeTab === tab ? '2px solid #58a6ff' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '11px',
                fontWeight: 'bold',
                fontFamily: '"Sometype Mono", monospace',
                textTransform: 'uppercase',
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Message */}
        {message && (
          <div
            style={{
              padding: '10px 16px',
              backgroundColor: message.type === 'success' ? '#1c3d2e' : '#3d1c1c',
              borderBottom: `1px solid ${message.type === 'success' ? '#3fb950' : '#f85149'}`,
              color: message.type === 'success' ? '#3fb950' : '#f85149',
              fontSize: '12px',
              fontWeight: 'bold',
            }}
          >
            {message.text}
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
          {activeTab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Upkeep Status */}
              <div style={{ backgroundColor: '#161b22', padding: '12px', border: '1px solid #30363d' }}>
                <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>UPKEEP STATUS</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#c9d1d9', fontSize: '12px' }}>Paid Until:</span>
                  <span style={{ color: claim.upkeepPaidUntil && new Date(claim.upkeepPaidUntil) > new Date() ? '#3fb950' : '#f85149', fontSize: '12px', fontWeight: 'bold' }}>
                    {formatTimeRemaining(claim.upkeepPaidUntil)}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#c9d1d9', fontSize: '12px' }}>Daily Cost:</span>
                  <span style={{ color: '#ffd700', fontSize: '12px' }}>
                    {totalDailyUpkeep.toLocaleString()}₱
                  </span>
                </div>
              </div>

              {/* Passive Income */}
              <div style={{ backgroundColor: '#161b22', padding: '12px', border: '1px solid #30363d' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <span style={{ color: '#8b949e', fontSize: '10px' }}>PASSIVE INCOME</span>
                  {hasIncome && (
                    <button
                      onClick={handleCollectIncome}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#238636',
                        color: '#ffffff',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        fontFamily: '"Sometype Mono", monospace',
                      }}
                    >
                      COLLECT
                    </button>
                  )}
                </div>
                <div style={{ color: '#ffd700', fontSize: '12px', marginBottom: '4px' }}>
                  {claim.accumulatedPieces.toLocaleString()}₱ accumulated
                </div>
                {Object.entries(claim.accumulatedMaterials).map(([material, amount]) => (
                  <div key={material} style={{ color: '#8b949e', fontSize: '11px' }}>
                    {material}: {amount}
                  </div>
                ))}
              </div>

              {/* Garrison Summary */}
              <div style={{ backgroundColor: '#161b22', padding: '12px', border: '1px solid #30363d' }}>
                <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>GARRISON</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                  <span style={{ color: '#c9d1d9', fontSize: '12px' }}>Units:</span>
                  <span style={{ color: '#58a6ff', fontSize: '12px' }}>
                    {garrison?.units.length || 0}/10
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#c9d1d9', fontSize: '12px' }}>Total Strength:</span>
                  <span style={{ color: '#3fb950', fontSize: '12px', fontWeight: 'bold' }}>
                    {garrison?.totalStrength || 0}
                  </span>
                </div>
              </div>

              {/* Territory Info */}
              <div style={{ backgroundColor: '#161b22', padding: '12px', border: '1px solid #30363d' }}>
                <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>TERRITORY INFO</div>
                <div style={{ color: '#c9d1d9', fontSize: '11px', marginBottom: '4px' }}>
                  Claimed: {formatDate(claim.claimedAt)}
                </div>
                <div style={{ color: '#c9d1d9', fontSize: '11px', marginBottom: '4px' }}>
                  Radius: {claim.claimRadius.toFixed(0)} units
                </div>
                {claim.assignedFaction && (
                  <div style={{ color: '#58a6ff', fontSize: '11px' }}>
                    Faction: {claim.assignedFaction}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'garrison' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {/* Current Units */}
              {garrison && garrison.units.length > 0 && (
                <div>
                  <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>DEPLOYED UNITS</div>
                  {garrison.units.map((unit) => {
                    const def = garrisonDefinitions[unit.type];
                    if (!def) return null;
                    const canUpgrade = unit.level < def.maxLevel;
                    const upgradeCost = def.hireCost * (unit.level + 1);

                    return (
                      <div
                        key={unit.id}
                        style={{
                          backgroundColor: '#161b22',
                          border: '1px solid #30363d',
                          padding: '12px',
                          marginBottom: '8px',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <div>
                            <span style={{ color: '#c9d1d9', fontSize: '13px', fontWeight: 'bold' }}>
                              {def.name}
                            </span>
                            <span style={{ color: '#8b949e', fontSize: '11px', marginLeft: '8px' }}>
                              Lv.{unit.level}
                            </span>
                          </div>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            {canUpgrade && (
                              <button
                                onClick={() => handleUpgradeGarrison(unit.id)}
                                disabled={playerPieces < upgradeCost}
                                style={{
                                  padding: '4px 8px',
                                  backgroundColor: playerPieces >= upgradeCost ? '#238636' : '#21262d',
                                  color: playerPieces >= upgradeCost ? '#ffffff' : '#8b949e',
                                  border: 'none',
                                  cursor: playerPieces >= upgradeCost ? 'pointer' : 'not-allowed',
                                  fontSize: '10px',
                                  fontWeight: 'bold',
                                  fontFamily: '"Sometype Mono", monospace',
                                }}
                              >
                                UPGRADE ({upgradeCost.toLocaleString()}₱)
                              </button>
                            )}
                            <button
                              onClick={() => handleDismissGarrison(unit.id)}
                              style={{
                                padding: '4px 8px',
                                backgroundColor: '#f85149',
                                color: '#ffffff',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '10px',
                                fontWeight: 'bold',
                                fontFamily: '"Sometype Mono", monospace',
                              }}
                            >
                              DISMISS
                            </button>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: '#8b949e' }}>
                          <span>Strength: <span style={{ color: '#3fb950' }}>{unit.strength}</span></span>
                          <span>Upkeep: <span style={{ color: '#ffd700' }}>{unit.upkeepCost}₱/day</span></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Hire Units */}
              <div>
                <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>HIRE GARRISON</div>
                {Object.values(garrisonDefinitions).map((def) => (
                  <div
                    key={def.type}
                    style={{
                      backgroundColor: '#161b22',
                      border: '1px solid #30363d',
                      padding: '12px',
                      marginBottom: '8px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                      <div>
                        <span style={{ color: '#c9d1d9', fontSize: '13px', fontWeight: 'bold' }}>
                          {def.name}
                        </span>
                        <div style={{ color: '#8b949e', fontSize: '11px', marginTop: '4px' }}>
                          {def.description}
                        </div>
                      </div>
                      <button
                        onClick={() => handleHireGarrison(def.type)}
                        disabled={playerPieces < def.hireCost || (garrison?.units.length || 0) >= 10}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: playerPieces >= def.hireCost && (garrison?.units.length || 0) < 10 ? '#238636' : '#21262d',
                          color: playerPieces >= def.hireCost && (garrison?.units.length || 0) < 10 ? '#ffffff' : '#8b949e',
                          border: 'none',
                          cursor: playerPieces >= def.hireCost && (garrison?.units.length || 0) < 10 ? 'pointer' : 'not-allowed',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          fontFamily: '"Sometype Mono", monospace',
                        }}
                      >
                        HIRE ({def.hireCost.toLocaleString()}₱)
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: '#8b949e' }}>
                      <span>Strength: <span style={{ color: '#3fb950' }}>{def.baseStrength}</span></span>
                      <span>Upkeep: <span style={{ color: '#ffd700' }}>{def.upkeepCost}₱/day</span></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'permissions' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ backgroundColor: '#161b22', padding: '12px', border: '1px solid #30363d' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ color: '#c9d1d9', fontSize: '13px' }}>Allow Allies</span>
                  <button
                    onClick={() => handleUpdatePermissions({ allowAllies: !claim.permissions.allowAllies })}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: claim.permissions.allowAllies ? '#238636' : '#21262d',
                      color: '#ffffff',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      fontFamily: '"Sometype Mono", monospace',
                    }}
                  >
                    {claim.permissions.allowAllies ? 'ENABLED' : 'DISABLED'}
                  </button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ color: '#c9d1d9', fontSize: '13px' }}>Allow Trading</span>
                  <button
                    onClick={() => handleUpdatePermissions({ allowTrading: !claim.permissions.allowTrading })}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: claim.permissions.allowTrading ? '#238636' : '#21262d',
                      color: '#ffffff',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      fontFamily: '"Sometype Mono", monospace',
                    }}
                  >
                    {claim.permissions.allowTrading ? 'ENABLED' : 'DISABLED'}
                  </button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <span style={{ color: '#c9d1d9', fontSize: '13px' }}>Allow Docking</span>
                  <button
                    onClick={() => handleUpdatePermissions({ allowDocking: !claim.permissions.allowDocking })}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: claim.permissions.allowDocking ? '#238636' : '#21262d',
                      color: '#ffffff',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      fontFamily: '"Sometype Mono", monospace',
                    }}
                  >
                    {claim.permissions.allowDocking ? 'ENABLED' : 'DISABLED'}
                  </button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#c9d1d9', fontSize: '13px' }}>PvP Enabled</span>
                  <button
                    onClick={() => handleUpdatePermissions({ pvpEnabled: !claim.permissions.pvpEnabled })}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: claim.permissions.pvpEnabled ? '#f85149' : '#238636',
                      color: '#ffffff',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      fontFamily: '"Sometype Mono", monospace',
                    }}
                  >
                    {claim.permissions.pvpEnabled ? 'ENABLED' : 'DISABLED'}
                  </button>
                </div>
              </div>

              <div style={{ backgroundColor: '#161b22', padding: '12px', border: '1px solid #30363d' }}>
                <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>TAX RATE</div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {[0, 5, 10, 15, 20].map((rate) => (
                    <button
                      key={rate}
                      onClick={() => handleUpdatePermissions({ taxRate: rate })}
                      style={{
                        flex: 1,
                        padding: '8px',
                        backgroundColor: claim.permissions.taxRate === rate ? '#58a6ff' : '#21262d',
                        color: '#ffffff',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        fontFamily: '"Sometype Mono", monospace',
                      }}
                    >
                      {rate}%
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'upgrades' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ color: '#8b949e', fontSize: '11px', marginBottom: '8px' }}>
                Territory upgrades coming soon...
              </div>
              {claim.upgrades.map((upgrade) => (
                <div
                  key={upgrade.type}
                  style={{
                    backgroundColor: '#161b22',
                    border: '1px solid #30363d',
                    padding: '12px',
                  }}
                >
                  <span style={{ color: '#c9d1d9', fontSize: '13px', fontWeight: 'bold' }}>
                    {upgrade.type}
                  </span>
                  <span style={{ color: '#8b949e', fontSize: '11px', marginLeft: '8px' }}>
                    Level {upgrade.level}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
